import { CheckIcon } from "@chakra-ui/icons";
import { Box, Button, HStack, Text, Tooltip, VStack } from "@chakra-ui/react";
import React from "react";
import { MapRef, Marker, NavigationControl, Popup, ScaleControl } from "react-map-gl/maplibre";

import BaseMap from "../../../../shared/components/BaseMap";
import { PatientId } from "../../../../shared/schema/schema";
import {
  MappableRnSchedulingMapPatient,
  SCHEDULING_STATUS_COLOR_SCHEME,
  SCHEDULING_STATUS_LABEL,
  SCHEDULING_STATUSES,
} from "../rn-scheduling-map.types";
import MapAreaSelectOverlay, { PixelRect } from "./MapAreaSelectOverlay";
import RnSchedulingMapPatientPopup from "./RnSchedulingMapPatientPopup";

/** Centered on New York City, where the map opens when nothing can be plotted. */
const FALLBACK_CENTER = { lat: 40.7128, lng: -74.006 };

type Props = {
  patients: MappableRnSchedulingMapPatient[];
  selectedPatientId: PatientId | null;
  isChecked: (patientId: PatientId) => boolean;
  onCheckPatients: (patientIds: PatientId[]) => void;
  onSelectPatient: (patientId: PatientId | null) => void;
  onToggleChecked: (patientId: PatientId) => void;
};

export default function RnSchedulingMapCanvas(props: Props) {
  const mapRef = React.useRef<MapRef | null>(null);
  const [isMapLoaded, setIsMapLoaded] = React.useState(false);
  const [isAreaSelectMode, setIsAreaSelectMode] = React.useState(false);
  const exitAreaSelectMode = React.useCallback(() => setIsAreaSelectMode(false), []);

  const selectedPatient =
    props.patients.find((patient) => patient.patientId === props.selectedPatientId) ?? null;

  const fitToPatients = React.useCallback((patients: MappableRnSchedulingMapPatient[]) => {
    const map = mapRef.current;

    if (map === null || patients.length === 0) {
      return;
    }

    const lats = patients.map((patient) => patient.location.lat);
    const lngs = patients.map((patient) => patient.location.lng);

    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 80, maxZoom: 14, duration: 600 }
    );
  }, []);

  // Re-frame the map whenever the set of plotted patients changes, so a new
  // filter selection is always fully visible.
  React.useEffect(() => {
    if (!isMapLoaded) {
      return;
    }

    fitToPatients(props.patients);
  }, [fitToPatients, isMapLoaded, props.patients]);

  React.useEffect(() => {
    const map = mapRef.current;

    if (map === null || selectedPatient === null) {
      return;
    }

    map.flyTo({
      center: [selectedPatient.location.lng, selectedPatient.location.lat],
      zoom: Math.max(map.getZoom(), 13),
      duration: 600,
    });
  }, [selectedPatient]);

  function handleSelectArea(rect: PixelRect) {
    const map = mapRef.current;

    if (map === null) {
      return;
    }

    const patientIdsInArea = props.patients
      .filter((patient) => {
        const point = map.project([patient.location.lng, patient.location.lat]);
        return (
          point.x >= rect.minX &&
          point.x <= rect.maxX &&
          point.y >= rect.minY &&
          point.y <= rect.maxY
        );
      })
      .map((patient) => patient.patientId);

    props.onCheckPatients(patientIdsInArea);
  }

  const initialCenter = props.patients[0]?.location ?? FALLBACK_CENTER;

  return (
    <Box borderRadius="md" height="100%" overflow="hidden" position="relative" width="100%">
      <BaseMap
        ref={mapRef}
        // Shift+drag draws a selection rectangle instead of the default box zoom.
        boxZoom={false}
        destination={initialCenter}
        style={{ width: "100%", height: "100%" }}
        onLoad={() => setIsMapLoaded(true)}
      >
        <NavigationControl position="top-right" showCompass={false} />
        <ScaleControl position="bottom-right" unit="imperial" />

        {props.patients.map((patient) => (
          <Marker
            key={patient.patientId}
            anchor="bottom"
            latitude={patient.location.lat}
            longitude={patient.location.lng}
            onClick={(event) => {
              event.originalEvent.stopPropagation();
              props.onSelectPatient(patient.patientId);
            }}
          >
            <PatientMapPin
              isChecked={props.isChecked(patient.patientId)}
              isSelected={patient.patientId === props.selectedPatientId}
              patient={patient}
            />
          </Marker>
        ))}

        {selectedPatient !== null && (
          <Popup
            anchor="top"
            closeButton={false}
            closeOnClick={false}
            latitude={selectedPatient.location.lat}
            longitude={selectedPatient.location.lng}
            maxWidth="320px"
            offset={12}
            onClose={() => props.onSelectPatient(null)}
          >
            <RnSchedulingMapPatientPopup
              isChecked={props.isChecked(selectedPatient.patientId)}
              patient={selectedPatient}
              onClose={() => props.onSelectPatient(null)}
              onToggleChecked={() => props.onToggleChecked(selectedPatient.patientId)}
            />
          </Popup>
        )}
      </BaseMap>

      <MapAreaSelectOverlay
        isAreaSelectMode={isAreaSelectMode}
        onExitAreaSelectMode={exitAreaSelectMode}
        onSelectArea={handleSelectArea}
      />

      <VStack align="stretch" left={3} position="absolute" spacing={2} top={3} zIndex={1}>
        <Button
          bg="white"
          boxShadow="md"
          isDisabled={props.patients.length === 0}
          size="sm"
          onClick={() => fitToPatients(props.patients)}
        >
          Fit to all patients
        </Button>
        <Tooltip
          label={
            isAreaSelectMode
              ? "Drag a rectangle on the map. Press Esc to cancel."
              : "Check every patient inside a rectangle. Tip: hold Shift and drag at any time."
          }
          placement="right"
        >
          <Button
            bg={isAreaSelectMode ? undefined : "white"}
            boxShadow="md"
            colorScheme={isAreaSelectMode ? "blue" : undefined}
            isDisabled={props.patients.length === 0}
            size="sm"
            onClick={() => setIsAreaSelectMode((value) => !value)}
          >
            {isAreaSelectMode ? "Cancel area select" : "Select area"}
          </Button>
        </Tooltip>
        <MapLegend />
      </VStack>
    </Box>
  );
}

function MapLegend() {
  return (
    <Box bg="white" borderRadius="md" boxShadow="md" fontSize="xs" px={3} py={2}>
      {SCHEDULING_STATUSES.map((status) => (
        <HStack key={status} spacing={2}>
          <Box
            bg={`${SCHEDULING_STATUS_COLOR_SCHEME[status]}.500`}
            borderRadius="full"
            height={3}
            width={3}
          />
          <Text>{SCHEDULING_STATUS_LABEL[status]}</Text>
        </HStack>
      ))}
    </Box>
  );
}

function PatientMapPin(props: {
  patient: MappableRnSchedulingMapPatient;
  isChecked: boolean;
  isSelected: boolean;
}) {
  const color = `${SCHEDULING_STATUS_COLOR_SCHEME[props.patient.schedulingStatus]}.500`;
  const size = props.isSelected ? 9 : 6;

  return (
    <Box alignItems="center" cursor="pointer" display="flex" flexDirection="column">
      <Box
        alignItems="center"
        bg={color}
        border="2px solid white"
        borderRadius="full"
        boxShadow={
          props.isChecked
            ? "0 0 0 2px var(--chakra-colors-gray-800), 0 1px 4px rgba(0,0,0,0.4)"
            : "0 1px 4px rgba(0,0,0,0.4)"
        }
        color="white"
        display="flex"
        height={size}
        justifyContent="center"
        transition="height 0.15s, width 0.15s"
        width={size}
      >
        {props.isChecked && <CheckIcon boxSize={props.isSelected ? 4 : 3} />}
      </Box>
      <Box
        borderLeft="5px solid transparent"
        borderRight="5px solid transparent"
        borderTop="7px solid white"
        height={0}
        marginTop="-2px"
        width={0}
      />
    </Box>
  );
}
